import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import Icon from '../../components/Icon';
import Modal from '../../components/Modal';
import { useToast } from '../../components/Toast';

/**
 * The platform console: our own book, not a customer's.
 *
 * Deliberately a different colour from the tenant console. Somebody with
 * both open needs to know at a glance which one they are typing a price
 * into, and a shared layout with a different logo is not enough.
 */

// ---------------------------------------------------------------------

interface Subscription {
  id: string;
  plan: { code: string; name: string } | null;
  billingPeriod: 'monthly' | 'yearly';
  pricePerUser: number;
  seats: number;
  status: 'trialing' | 'active' | 'past_due' | 'cancelled';
  startedOn: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialEndsOn: string | null;
  cancelReason: string | null;
  notes: string | null;
  contractValue: number;
  monthlyValue: number;
}

interface CompanyRow {
  id: string;
  name: string;
  slug: string;
  gstin: string | null;
  industry: string | null;
  status: string;
  signedUpOn: string | null;
  users: number;
  fieldUsers: number;
  lastLoginAt: string | null;
  outstanding: number;
  overSeats: number;
  subscription: Subscription | null;
}

interface Invoice {
  id: string;
  invoiceNo: string;
  status: 'draft' | 'sent' | 'paid' | 'void';
  periodStart: string;
  periodEnd: string;
  seats: number;
  pricePerUser: number;
  subtotal: number;
  discount: number;
  taxable: number;
  gstRate: number;
  cgst: number;
  sgst: number;
  igst: number;
  total: number;
  isInterstate: boolean;
  issuedOn: string | null;
  dueOn: string | null;
  paidOn: string | null;
  paymentRef: string | null;
}

interface CompanyDetail extends CompanyRow {
  businessModel: 'goods' | 'service';
  dispatchEnabled: boolean;
  customersEnabled: boolean;
  leadsEnabled: boolean;
  assistantEnabled: boolean;
  distributorEnabled: boolean;
  distributorPricePerLogin: number;
  invoices: Invoice[];
  admins: { id: string; fullName: string; email: string | null; status: string; lastLoginAt: string | null }[];
  history: { kind: string; data: Record<string, unknown>; at: string; actor: string | null }[];
}

interface ModePreview {
  to: 'goods' | 'service';
  consequences: string[];
  customers: { total: number; withoutLocation: number };
}

interface Summary {
  companies: { total: number; trialing: number; active: number; past_due: number; cancelled: number };
  revenue: { mrr: number; arr: number; payingMrr: number; payingArr: number };
  receivables: { outstanding: number; openInvoices: number; overdue: number; overdueInvoices: number };
  trialsEnding: { companyId: string; name: string; endsOn: string; daysLeft: number; monthlyValue: number }[];
  renewalsDue: { companyId: string; name: string; dueOn: string; daysLeft: number; amount: number }[];
  staleRenewals: number;
}

interface Plan { code: string; name: string; price: number; billingPeriod: string; trialDays: number }

// ---------------------------------------------------------------------

/** Indian digit grouping. 12,34,567 reads as a different number to 1,234,567. */
const money = (n: number) => `₹${(n ?? 0).toLocaleString('en-IN', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

/** For figures on a dashboard, where two decimals are noise. */
const short = (n: number) => {
  if (Math.abs(n) >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (Math.abs(n) >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
};

const date = (iso: string | null) => (iso
  ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
  : '—');

const STATUS_TONE: Record<string, string> = {
  trialing: 'info',
  active: 'success',
  invited: 'info',
  pending_verification: 'warning',
  deactivated: 'muted',
  past_due: 'warning',
  suspended: 'danger',
  cancelled: 'muted',
  draft: 'muted',
  sent: 'warning',
  paid: 'success',
  void: 'muted',
};

const STATUS_LABEL: Record<string, string> = {
  trialing: 'Trial',
  active: 'Active',
  invited: 'Invited',
  pending_verification: 'Unverified',
  deactivated: 'Deactivated',
  past_due: 'Past due',
  suspended: 'Suspended',
  cancelled: 'Cancelled',
  draft: 'Draft',
  sent: 'Sent',
  paid: 'Paid',
  void: 'Void',
};

function Pill({ value }: { value: string }) {
  return (
    <span className={`pf-pill pf-pill-${STATUS_TONE[value] ?? 'muted'}`}>
      {STATUS_LABEL[value] ?? value}
    </span>
  );
}

// ---------------------------------------------------------------------

export default function PlatformPage() {
  const { user, signOut, impersonate } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [entering, setEntering] = useState<string | null>(null);

  const [tab, setTab] = useState<'companies' | 'revenue' | 'settings'>('companies');
  const [summary, setSummary] = useState<Summary | null>(null);
  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [registering, setRegistering] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, c, p] = await Promise.all([
        api.get<Summary>('/platform/summary'),
        api.get<{ companies: CompanyRow[] }>('/platform/companies?limit=200'),
        api.get<Plan[]>('/platform/plans'),
      ]);
      setSummary(s);
      setCompanies(c.companies);
      setPlans(p);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the platform console');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const enterAs = useCallback(async (companyId: string, _companyName: string) => {
    setEntering(companyId);
    try {
      await impersonate(companyId);
      navigate('/customers/form');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not sign in as that company');
    } finally {
      setEntering(null);
    }
  }, [impersonate, navigate, toast]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return companies;
    return companies.filter((c) => c.name.toLowerCase().includes(needle)
      || c.slug.toLowerCase().includes(needle));
  }, [companies, search]);

  return (
    <div className="pf">
      <header className="pf-top">
        <div className="pf-brand">
          <span className="pf-mark">S</span>
          <div>
            <strong>Salestrendz</strong>
            <span>Platform console</span>
          </div>
        </div>
        <div className="pf-who">
          <span>{user?.fullName}</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={signOut}>
            <Icon name="logout" /> Sign out
          </button>
        </div>
      </header>

      <div className="pf-body">
        <div className="pf-tabs">
          {(['companies', 'revenue', 'settings'] as const).map((t) => (
            <button
              key={t}
              type="button"
              className={`pf-tab${tab === t ? ' active' : ''}`}
              onClick={() => setTab(t)}
            >
              {t === 'companies' ? 'Companies' : t === 'revenue' ? 'Revenue' : 'Invoice settings'}
            </button>
          ))}
          <div className="pf-tab-spacer" />
          <button type="button" className="btn btn-primary" onClick={() => setRegistering(true)}>
            <Icon name="plus" /> Register a company
          </button>
        </div>

        {error ? (
          <div className="pf-card pf-error">
            <strong>Could not load the console</strong>
            <p>{error}</p>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => void load()}>
              Try again
            </button>
          </div>
        ) : null}

        {loading && !summary ? <div className="pf-card">Loading…</div> : null}

        {summary ? <StatRow summary={summary} /> : null}

        {tab === 'companies' && summary ? (
          <div className="pf-card">
            <div className="pf-card-head">
              <h2>Companies signed up</h2>
              <input
                className="input pf-search"
                placeholder="Search by name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {visible.length === 0 ? (
              <p className="pf-empty">
                {search ? `Nothing matches “${search}”.` : 'No companies yet.'}
              </p>
            ) : (
              <div className="pf-table-wrap">
                <table className="pf-table">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Signed up</th>
                      <th>Status</th>
                      <th className="num">Users</th>
                      <th className="num">Seats</th>
                      <th className="num">Rate</th>
                      <th>Period</th>
                      <th className="num">Per month</th>
                      <th className="num">Owed</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((c) => (
                      <tr key={c.id} onClick={() => setOpenId(c.id)} className="pf-row">
                        <td>
                          <strong>{c.name}</strong>
                          <span className="pf-sub">{c.slug}</span>
                        </td>
                        <td>{date(c.signedUpOn)}</td>
                        <td><Pill value={c.status} /></td>
                        <td className="num">
                          {c.users}
                          {c.overSeats > 0 ? (
                            <span className="pf-over" title={`${c.overSeats} more field users than seats paid for`}>
                              +{c.overSeats}
                            </span>
                          ) : null}
                        </td>
                        <td className="num">{c.subscription?.seats ?? '—'}</td>
                        <td className="num">{c.subscription ? money(c.subscription.pricePerUser) : '—'}</td>
                        <td>{c.subscription?.billingPeriod === 'yearly' ? 'Yearly' : 'Monthly'}</td>
                        <td className="num strong">{c.subscription ? money(c.subscription.monthlyValue) : '—'}</td>
                        <td className="num">{c.outstanding > 0 ? money(c.outstanding) : '—'}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            disabled={entering === c.id}
                            onClick={(e) => { e.stopPropagation(); void enterAs(c.id, c.name); }}
                          >
                            {entering === c.id ? 'Entering…' : 'Sign in as'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null}

        {tab === 'revenue' && summary ? <RevenueTab summary={summary} onOpen={setOpenId} /> : null}
        {tab === 'settings' ? <SettingsTab /> : null}
      </div>

      {registering ? (
        <RegisterCompany
          plans={plans}
          onClose={() => setRegistering(false)}
          onDone={(name) => {
            setRegistering(false);
            toast.success(`${name} is signed up`);
            void load();
          }}
        />
      ) : null}

      {openId ? (
        <CompanyDetailModal
          companyId={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => void load()}
          onEnterAs={enterAs}
          entering={entering}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------

function StatRow({ summary }: { summary: Summary }) {
  const { companies, revenue, receivables } = summary;
  return (
    <div className="pf-stats">
      <div className="pf-stat">
        <span className="pf-stat-label">Companies</span>
        <strong>{companies.total}</strong>
        <span className="pf-stat-note">
          {companies.active} active · {companies.trialing} on trial
        </span>
      </div>
      <div className="pf-stat">
        <span className="pf-stat-label">Signed MRR</span>
        <strong>{short(revenue.mrr)}</strong>
        <span className="pf-stat-note">{short(revenue.arr)} a year</span>
      </div>
      <div className="pf-stat">
        {/* Trials are not revenue until somebody agrees to pay. Quoting
            only the signed figure is how a pipeline gets mistaken for it. */}
        <span className="pf-stat-label">Paying MRR</span>
        <strong>{short(revenue.payingMrr)}</strong>
        <span className="pf-stat-note">excludes trials</span>
      </div>
      <div className={`pf-stat${receivables.overdue > 0 ? ' pf-stat-warn' : ''}`}>
        <span className="pf-stat-label">Outstanding</span>
        <strong>{short(receivables.outstanding)}</strong>
        <span className="pf-stat-note">
          {receivables.overdue > 0
            ? `${short(receivables.overdue)} overdue`
            : `${receivables.openInvoices} open invoice${receivables.openInvoices === 1 ? '' : 's'}`}
        </span>
      </div>
    </div>
  );
}

function RevenueTab({ summary, onOpen }: { summary: Summary; onOpen: (id: string) => void }) {
  return (
    <div className="pf-grid">
      <div className="pf-card">
        <div className="pf-card-head"><h2>Trials ending</h2></div>
        {summary.trialsEnding.length === 0 ? (
          <p className="pf-empty">Nobody is on a trial right now.</p>
        ) : summary.trialsEnding.map((t) => (
          <button key={t.companyId} type="button" className="pf-line" onClick={() => onOpen(t.companyId)}>
            <span>
              <strong>{t.name}</strong>
              <span className="pf-sub">
                {t.daysLeft < 0 ? `ended ${Math.abs(t.daysLeft)}d ago` : `${t.daysLeft} days left`}
                {' · '}{date(t.endsOn)}
              </span>
            </span>
            <span className="num">{money(t.monthlyValue)}/mo</span>
          </button>
        ))}
      </div>

      <div className="pf-card">
        <div className="pf-card-head"><h2>Renewals due</h2></div>
        {summary.renewalsDue.length === 0 ? (
          <p className="pf-empty">Nothing renews in the next month.</p>
        ) : summary.renewalsDue.map((t) => (
          <button key={t.companyId} type="button" className="pf-line" onClick={() => onOpen(t.companyId)}>
            <span>
              <strong>{t.name}</strong>
              <span className="pf-sub">
                {date(t.dueOn)}
                {' · '}
                {t.daysLeft < 0
                  ? `${Math.abs(t.daysLeft)} days overdue`
                  : t.daysLeft === 0 ? 'today' : `in ${t.daysLeft} days`}
              </span>
            </span>
            <span className="num">{money(t.amount)}</span>
          </button>
        ))}
        {summary.staleRenewals > 0 ? (
          <p className="pf-hint" style={{ marginTop: 12, marginBottom: 0 }}>
            {summary.staleRenewals} live {summary.staleRenewals === 1 ? 'account has' : 'accounts have'}
            {' '}a period that ended over a month ago and was never renewed.
          </p>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------

function RegisterCompany({ plans, onClose, onDone }: {
  plans: Plan[];
  onClose: () => void;
  onDone: (name: string) => void;
}) {
  const [form, setForm] = useState({
    name: '', gstin: '', industry: '',
    adminName: '', adminEmail: '', adminMobile: '',
    planCode: plans[0]?.code ?? '', billingPeriod: 'monthly' as 'monthly' | 'yearly',
    pricePerUser: '', seats: '10', trialDays: '14', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setFields((f) => { const { [k]: _drop, ...rest } = f; return rest; });
  };

  const plan = plans.find((p) => p.code === form.planCode);
  const rate = Number(form.pricePerUser || plan?.price || 0);
  const seats = Number(form.seats || 0);
  const perPeriod = rate * seats;

  const submit = async () => {
    setSaving(true);
    setError(null);
    setFields({});
    try {
      const body: Record<string, unknown> = {
        name: form.name.trim(),
        billingPeriod: form.billingPeriod,
        seats: Number(form.seats),
      };
      if (form.gstin.trim()) body.gstin = form.gstin.trim().toUpperCase();
      if (form.industry.trim()) body.industry = form.industry.trim();
      if (form.adminName.trim()) body.adminName = form.adminName.trim();
      if (form.adminEmail.trim()) body.adminEmail = form.adminEmail.trim();
      if (form.adminMobile.trim()) body.adminMobile = form.adminMobile.trim();
      if (form.planCode) body.planCode = form.planCode;
      if (form.pricePerUser !== '') body.pricePerUser = Number(form.pricePerUser);
      if (form.trialDays !== '') body.trialDays = Number(form.trialDays);
      if (form.notes.trim()) body.notes = form.notes.trim();

      await api.post('/platform/companies', body);
      onDone(form.name.trim());
    } catch (err) {
      const e = err as { message?: string; fields?: Record<string, string> };
      setError(e.message ?? 'Could not register the company');
      if (e.fields) setFields(e.fields);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Register a company"
      onClose={onClose}
      wide
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            type="button" className="btn btn-primary"
            disabled={saving || form.name.trim().length < 2}
            onClick={() => void submit()}
          >
            {saving ? 'Registering…' : 'Register company'}
          </button>
        </>
      )}
    >
      <div className="pf-form">
        {error ? <div className="pf-form-error">{error}</div> : null}

        <h3>The company</h3>
        <div className="pf-fields">
          <label>
            <span>Company name</span>
            <input className="input" value={form.name} onChange={set('name')} placeholder="Verma Distributors" autoFocus />
            {fields.name ? <em>{fields.name}</em> : null}
          </label>
          <label>
            <span>GSTIN <small>optional</small></span>
            <input className="input" value={form.gstin} onChange={set('gstin')} placeholder="27AAPFU0939F1ZV" maxLength={15} />
            {fields.gstin ? <em>{fields.gstin}</em> : null}
            <small>Decides CGST/SGST or IGST on their invoices.</small>
          </label>
          <label>
            <span>Industry <small>optional</small></span>
            <input className="input" value={form.industry} onChange={set('industry')} placeholder="FMCG" />
          </label>
        </div>

        <h3>Their administrator</h3>
        <p className="pf-hint">
          They get an invitation and choose their own password. Nobody here ever knows it.
        </p>
        <div className="pf-fields">
          <label>
            <span>Name</span>
            <input className="input" value={form.adminName} onChange={set('adminName')} placeholder="Rakesh Verma" />
          </label>
          <label>
            <span>Email</span>
            <input className="input" value={form.adminEmail} onChange={set('adminEmail')} placeholder="rakesh@verma.co.in" />
            {fields.adminEmail ? <em>{fields.adminEmail}</em> : null}
          </label>
          <label>
            <span>Mobile <small>optional</small></span>
            <input className="input" value={form.adminMobile} onChange={set('adminMobile')} placeholder="9820011234" />
          </label>
        </div>

        <h3>What they signed up for</h3>
        <div className="pf-fields">
          <label>
            <span>Plan</span>
            <select className="input" value={form.planCode} onChange={set('planCode')}>
              <option value="">No plan</option>
              {plans.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
            </select>
          </label>
          <label>
            <span>Billing period</span>
            <select className="input" value={form.billingPeriod} onChange={set('billingPeriod')}>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </label>
          <label>
            <span>Rate per user</span>
            <input
              className="input" type="number" min="0" step="0.01"
              value={form.pricePerUser} onChange={set('pricePerUser')}
              placeholder={plan ? String(plan.price) : '0'}
            />
            {fields.pricePerUser ? <em>{fields.pricePerUser}</em> : null}
            <small>Leave blank to use the plan's list price.</small>
          </label>
          <label>
            <span>Seats</span>
            <input className="input" type="number" min="1" value={form.seats} onChange={set('seats')} />
            {fields.seats ? <em>{fields.seats}</em> : null}
          </label>
          <label>
            <span>Trial days</span>
            <input className="input" type="number" min="0" value={form.trialDays} onChange={set('trialDays')} />
            <small>Zero starts them as active straight away.</small>
          </label>
        </div>

        <label className="pf-full">
          <span>Notes <small>optional</small></span>
          <textarea
            className="input" rows={2} value={form.notes} onChange={set('notes')}
            placeholder="Negotiated down from list; 25 reps to start."
          />
        </label>

        <div className="pf-total">
          <span>{seats || 0} seats × {money(rate)}</span>
          <strong>{money(perPeriod)} per {form.billingPeriod === 'yearly' ? 'year' : 'month'}</strong>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------

/**
 * The company's business model — goods vs service — chosen by the platform.
 * Switching previews the consequences first (the same preview the tenant
 * console used to show), because it changes what every rep sees.
 */
function BusinessType({ companyId, current, onChanged }: {
  companyId: string;
  current: 'goods' | 'service';
  onChanged: () => void;
}) {
  const toast = useToast();
  const [preview, setPreview] = useState<ModePreview | null>(null);
  const [busy, setBusy] = useState(false);

  const OPTS: { value: 'goods' | 'service'; title: string; blurb: string }[] = [
    { value: 'goods', title: 'Goods / Distribution', blurb: 'Sells to a fixed set of shops — a visit is against a customer.' },
    { value: 'service', title: 'Service / Field work', blurb: 'Goes where the job is — a visit is a place and a time on the map.' },
  ];

  async function choose(to: 'goods' | 'service') {
    if (to === current || busy) return;
    setBusy(true);
    try {
      setPreview(await api.post<ModePreview>(
        `/platform/companies/${companyId}/preview-mode`, { businessModel: to },
      ));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not check that.');
    } finally {
      setBusy(false);
    }
  }

  async function apply() {
    if (!preview) return;
    setBusy(true);
    try {
      await api.patch(`/platform/companies/${companyId}/business-model`, { businessModel: preview.to });
      toast.success(`Switched to ${preview.to === 'service' ? 'service' : 'goods'}.`);
      setPreview(null);
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not switch that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="col gap-8" style={{ marginBottom: 10 }}>
      <div className="pf-modes">
        {OPTS.map((o) => {
          const active = o.value === current;
          return (
            <button
              key={o.value}
              type="button"
              disabled={busy}
              className={`pf-mode${active ? ' active' : ''}`}
              onClick={() => void choose(o.value)}
            >
              <span className="pf-mode-title">
                {o.title}{active ? ' · in use' : ''}
              </span>
              <span className="pf-mode-blurb">{o.blurb}</span>
            </button>
          );
        })}
      </div>

      {preview ? (
        <div className="pf-mode-confirm">
          <strong>Switch to {preview.to === 'service' ? 'service' : 'goods'}?</strong>
          <ul className="pf-mode-consequences">
            {preview.consequences.map((line) => <li key={line}>{line}</li>)}
          </ul>
          {preview.to === 'service' && preview.customers.withoutLocation > 0 ? (
            <p className="small">
              {preview.customers.withoutLocation} of {preview.customers.total} customers
              have no location yet — nothing is deleted; they just will not show on the
              map until a pin is captured.
            </p>
          ) : null}
          <div className="pf-row-actions">
            <button type="button" className="btn btn-secondary btn-sm" disabled={busy}
              onClick={() => setPreview(null)}>Cancel</button>
            <button type="button" className="btn btn-primary btn-sm" disabled={busy}
              onClick={() => void apply()}>Switch</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Per-company modules the platform switches on. Dispatch/delivery is the
 * first: off by default, and invisible to the company until turned on.
 */
function ModuleToggles({ companyId, dispatchEnabled, customersEnabled, leadsEnabled, assistantEnabled, distributorEnabled, distributorPricePerLogin, onChanged }: {
  companyId: string;
  dispatchEnabled: boolean;
  customersEnabled: boolean;
  leadsEnabled: boolean;
  assistantEnabled: boolean;
  distributorEnabled: boolean;
  distributorPricePerLogin: number;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [distPrice, setDistPrice] = useState<string>(String(distributorPricePerLogin ?? 0));
  const [editingPrice, setEditingPrice] = useState(false);

  async function call(fn: () => Promise<unknown>, okMsg: string) {
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not change that.');
    } finally {
      setBusy(false);
    }
  }

  async function saveDistPrice() {
    const val = parseFloat(distPrice);
    if (isNaN(val) || val < 0) return;
    await call(
      () => api.patch(`/platform/companies/${companyId}/distributor-price`, { pricePerLogin: val }),
      'Distributor login cost updated.');
    setEditingPrice(false);
  }

  const row = (
    title: string, blurb: string, on: boolean, toggle: () => void,
  ) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <strong>{title}</strong>
        <p className="small" style={{ margin: 0, opacity: 0.75 }}>{blurb}</p>
      </div>
      <button type="button" className={`btn btn-sm ${on ? 'btn-secondary' : 'btn-primary'}`}
        disabled={busy} onClick={toggle}>
        {on ? 'Turn off' : 'Turn on'}
      </button>
      <span className="small" style={{ minWidth: 56, textAlign: 'right' }}>{on ? 'On' : 'Off'}</span>
    </div>
  );

  return (
    <div className="col">
      {row('Customers', 'The customer master — shops your reps file and order against. A company must keep at least one of Customers or Leads.',
        customersEnabled, () => void call(
          () => api.patch(`/platform/companies/${companyId}/modules`, { customersEnabled: !customersEnabled }),
          !customersEnabled ? 'Customers turned on.' : 'Customers turned off.'))}
      {row('Leads / CRM', 'A pipeline of prospects with custom stages, statuses and form — convert a won lead into a customer.',
        leadsEnabled, () => void call(
          () => api.patch(`/platform/companies/${companyId}/modules`, { leadsEnabled: !leadsEnabled }),
          !leadsEnabled ? 'Leads turned on.' : 'Leads turned off.'))}
      {row('Dispatch / delivery', 'Dispatch sheets, a Delivery role, and proof-of-delivery capture.',
        dispatchEnabled, () => void call(
          () => api.patch(`/platform/companies/${companyId}/dispatch`, { enabled: !dispatchEnabled }),
          !dispatchEnabled ? 'Dispatch module turned on.' : 'Dispatch module turned off.'))}
      {row('Ask AI', 'Managers and admins ask questions in plain language and get answers from their own data (attendance, orders, deliveries, visits, customers, expenses, tracking). Each question costs a model call — bill it as an add-on.',
        assistantEnabled, () => void call(
          () => api.patch(`/platform/companies/${companyId}/modules`, { assistantEnabled: !assistantEnabled }),
          !assistantEnabled ? 'Ask AI turned on.' : 'Ask AI turned off.'))}
      {row('Distributors', 'Intermediaries between the company and its retailers. Distributors get their own portal with secondary-order visibility, primary-order placement, and retailer lists.',
        distributorEnabled, () => void call(
          () => api.patch(`/platform/companies/${companyId}/distributor`, { enabled: !distributorEnabled }),
          !distributorEnabled ? 'Distributor module turned on.' : 'Distributor module turned off.'))}

      {distributorEnabled ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0 10px 16px', borderLeft: '2px solid var(--border, #e0e0e0)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span className="small faint">Login cost per distributor / month</span>
          </div>
          {editingPrice ? (
            <>
              <span className="small">₹</span>
              <input
                type="number" min="0" step="1" className="input"
                style={{ width: 100 }} value={distPrice}
                onChange={(e) => setDistPrice(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void saveDistPrice(); if (e.key === 'Escape') setEditingPrice(false); }}
                autoFocus
              />
              <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => void saveDistPrice()}>
                Save
              </button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setDistPrice(String(distributorPricePerLogin ?? 0)); setEditingPrice(false); }}>
                Cancel
              </button>
            </>
          ) : (
            <>
              <span className="small" style={{ fontWeight: 600 }}>₹{(distributorPricePerLogin ?? 0).toLocaleString('en-IN')}</span>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setDistPrice(String(distributorPricePerLogin ?? 0)); setEditingPrice(true); }}>
                Edit
              </button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ── Users & passwords (platform-level) ──────────────────────────────

const ROLE_LABEL: Record<string, string> = {
  platform_admin: 'Platform Admin',
  company_admin: 'Admin',
  manager: 'Manager',
  sales_rep: 'Salesman',
  delivery: 'Delivery',
};

interface PlatformUser {
  id: string;
  fullName: string;
  email: string | null;
  mobile: string | null;
  role: string;
  status: string;
  lastLoginAt: string | null;
  hasPassword: boolean;
  reportsTo: string | null;
}

function CompanyUsers({ companyId }: { companyId: string }) {
  const toast = useToast();
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState<string | null>(null);
  const [newPw, setNewPw] = useState('');
  const [lastReset, setLastReset] = useState<{ userId: string; password: string } | null>(null);
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<PlatformUser[]>(`/platform/companies/${companyId}/users`);
      setUsers(data);
    } catch {
      toast.error('Could not load users');
    } finally {
      setLoading(false);
    }
  }, [companyId, toast]);

  useEffect(() => { void load(); }, [load]);

  const resetPassword = async (userId: string) => {
    if (!newPw.trim()) return;
    setBusy(true);
    try {
      await api.post(`/platform/companies/${companyId}/users/${userId}/reset-password`, {
        password: newPw,
      });
      setLastReset({ userId, password: newPw });
      setResetting(null);
      setNewPw('');
      toast.success('Password updated');
      void load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not reset password');
    } finally {
      setBusy(false);
    }
  };

  const generateSimplePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let pw = '';
    for (let i = 0; i < 10; i++) pw += chars[Math.floor(Math.random() * chars.length)];
    // Ensure at least one digit
    pw = pw.slice(0, 8) + String(Math.floor(Math.random() * 90) + 10);
    setNewPw(pw);
  };

  if (loading) return <p className="pf-empty">Loading users…</p>;

  return (
    <div className="pf-table-wrap">
      <table className="pf-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Role</th>
            <th>Login</th>
            <th>Status</th>
            <th>Last sign-in</th>
            <th>Password</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id}>
              <td>
                <strong>{u.fullName}</strong>
                {u.reportsTo ? <span className="pf-sub">→ {u.reportsTo}</span> : null}
              </td>
              <td>{ROLE_LABEL[u.role] ?? u.role}</td>
              <td>
                {u.email ? <span>{u.email}</span> : null}
                {u.mobile ? <span className={u.email ? 'pf-sub' : ''}>{u.mobile}</span> : null}
              </td>
              <td><Pill value={u.status} /></td>
              <td>{u.lastLoginAt ? date(u.lastLoginAt) : 'Never'}</td>
              <td>
                {lastReset?.userId === u.id ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <code style={{ fontSize: 13, background: 'var(--bg-success, #e8f5e9)', padding: '2px 8px', borderRadius: 4 }}>
                      {showPw ? lastReset.password : '••••••••'}
                    </code>
                    <button type="button" className="btn btn-ghost btn-sm"
                      style={{ padding: '2px 6px', lineHeight: 1 }}
                      onClick={() => setShowPw(!showPw)} title={showPw ? 'Hide' : 'Show'}>
                      <Icon name="eye" />
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm"
                      style={{ padding: '2px 6px', lineHeight: 1 }}
                      onClick={() => { void navigator.clipboard.writeText(lastReset.password); toast.success('Copied'); }}
                      title="Copy">
                      <Icon name="copy" />
                    </button>
                  </span>
                ) : resetting === u.id ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <input
                      className="input" type="text" value={newPw}
                      onChange={(e) => setNewPw(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') void resetPassword(u.id); if (e.key === 'Escape') { setResetting(null); setNewPw(''); } }}
                      placeholder="New password" style={{ width: 140, fontSize: 13 }} autoFocus
                    />
                    <button type="button" className="btn btn-ghost btn-sm"
                      style={{ padding: '2px 6px', lineHeight: 1 }}
                      onClick={generateSimplePassword} title="Generate">
                      ⟳
                    </button>
                    <button type="button" className="btn btn-primary btn-sm" disabled={busy || newPw.length < 8}
                      onClick={() => void resetPassword(u.id)}>Set</button>
                    <button type="button" className="btn btn-secondary btn-sm"
                      onClick={() => { setResetting(null); setNewPw(''); }}>✕</button>
                  </span>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span className="pf-sub">{u.hasPassword ? 'Set' : u.email ? 'Not set' : 'OTP only'}</span>
                    {u.email ? (
                      <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 12 }}
                        onClick={() => { setResetting(u.id); setLastReset(null); setNewPw(''); }}>
                        Reset
                      </button>
                    ) : null}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CompanyDetailModal({ companyId, onClose, onChanged, onEnterAs, entering }: {
  companyId: string;
  onClose: () => void;
  onChanged: () => void;
  onEnterAs: (companyId: string, companyName: string) => void;
  entering: string | null;
}) {
  const toast = useToast();
  const [company, setCompany] = useState<CompanyDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState('');
  const [terms, setTerms] = useState({ pricePerUser: '', seats: '', billingPeriod: 'monthly', status: 'active' });

  const load = useCallback(async () => {
    const data = await api.get<CompanyDetail>(`/platform/companies/${companyId}`);
    setCompany(data);
    if (data.subscription) {
      setTerms({
        pricePerUser: String(data.subscription.pricePerUser),
        seats: String(data.subscription.seats),
        billingPeriod: data.subscription.billingPeriod,
        status: data.subscription.status,
      });
    }
  }, [companyId]);

  useEffect(() => { void load(); }, [load]);

  const act = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true);
    try {
      await fn();
      await load();
      onChanged();
      toast.success(done);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'That did not work');
    } finally {
      setBusy(false);
    }
  };

  const saveName = async () => {
    const trimmed = nameVal.trim();
    if (!trimmed || trimmed.length < 2) { toast.error('Give the company a name'); return; }
    await act(
      () => api.patch(`/platform/companies/${companyId}/name`, { name: trimmed }),
      'Company renamed.',
    );
    setEditingName(false);
  };

  if (!company) return <Modal title="Loading…" onClose={onClose}><p>Loading…</p></Modal>;

  const sub = company.subscription;

  return (
    <Modal title={editingName ? 'Rename company' : company.name} onClose={onClose} wide>
      <div className="pf-detail">
        <div className="pf-detail-head">
          <div>
            {editingName ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <input
                  className="input" type="text" value={nameVal}
                  onChange={(e) => setNameVal(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void saveName(); if (e.key === 'Escape') setEditingName(false); }}
                  style={{ flex: 1, maxWidth: 320 }} autoFocus
                />
                <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => void saveName()}>Save</button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditingName(false)}>Cancel</button>
              </div>
            ) : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <button
                  type="button" className="btn btn-secondary btn-sm"
                  title="Rename company"
                  style={{ padding: '2px 6px', lineHeight: 1 }}
                  onClick={() => { setNameVal(company.name); setEditingName(true); }}
                >
                  <Icon name="edit" />
                </button>
              </span>
            )}
            <Pill value={company.status} />
            <span className="pf-sub">
              {company.slug} · signed up {date(company.signedUpOn)}
              {company.gstin ? ` · ${company.gstin}` : ''}
            </span>
          </div>
          <div className="pf-detail-value">
            <strong>{sub ? money(sub.monthlyValue) : '—'}</strong>
            <span>per month</span>
          </div>
        </div>

        <button
          type="button"
          className="btn btn-primary"
          disabled={entering === companyId}
          onClick={() => onEnterAs(companyId, company.name)}
          style={{ alignSelf: 'flex-start', marginBottom: 12 }}
        >
          <Icon name="eye" />
          {entering === companyId ? 'Entering…' : `Sign in as ${company.name}`}
        </button>

        {company.overSeats > 0 ? (
          <div className="pf-notice">
            <Icon name="alert" />
            <div>
              <strong>{company.overSeats} more field users than seats</strong>
              <p>
                They are paying for {sub?.seats} and using {company.fieldUsers}. Worth a
                conversation rather than a block.
              </p>
            </div>
          </div>
        ) : null}

        <h3>Business type</h3>
        <BusinessType
          companyId={companyId}
          current={company.businessModel}
          onChanged={() => { void load(); onChanged(); }}
        />

        <h3>Modules</h3>
        <ModuleToggles
          companyId={companyId}
          dispatchEnabled={company.dispatchEnabled}
          customersEnabled={company.customersEnabled}
          leadsEnabled={company.leadsEnabled}
          assistantEnabled={company.assistantEnabled}
          distributorEnabled={company.distributorEnabled}
          distributorPricePerLogin={company.distributorPricePerLogin}
          onChanged={() => { void load(); onChanged(); }}
        />

        <h3>Terms</h3>
        {sub ? (
          editing ? (
            <>
              <div className="pf-fields">
                <label>
                  <span>Rate per user</span>
                  <input
                    className="input" type="number" min="0" step="0.01" value={terms.pricePerUser}
                    onChange={(e) => setTerms((t) => ({ ...t, pricePerUser: e.target.value }))}
                  />
                </label>
                <label>
                  <span>Seats</span>
                  <input
                    className="input" type="number" min="1" value={terms.seats}
                    onChange={(e) => setTerms((t) => ({ ...t, seats: e.target.value }))}
                  />
                </label>
                <label>
                  <span>Billing period</span>
                  <select
                    className="input" value={terms.billingPeriod}
                    onChange={(e) => setTerms((t) => ({ ...t, billingPeriod: e.target.value }))}
                  >
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </label>
                <label>
                  <span>Status</span>
                  <select
                    className="input" value={terms.status}
                    onChange={(e) => setTerms((t) => ({ ...t, status: e.target.value }))}
                  >
                    <option value="trialing">Trial</option>
                    <option value="active">Active</option>
                    <option value="past_due">Past due</option>
                  </select>
                </label>
              </div>
              <div className="pf-row-actions">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(false)}>
                  Cancel
                </button>
                <button
                  type="button" className="btn btn-primary btn-sm" disabled={busy}
                  onClick={() => act(async () => {
                    await api.patch(`/platform/companies/${companyId}/subscription`, {
                      pricePerUser: Number(terms.pricePerUser),
                      seats: Number(terms.seats),
                      billingPeriod: terms.billingPeriod,
                      status: terms.status,
                    });
                    setEditing(false);
                  }, 'Terms updated')}
                >
                  Save terms
                </button>
              </div>
            </>
          ) : (
            <>
              <dl className="pf-terms">
                <div><dt>Plan</dt><dd>{sub.plan?.name ?? 'No plan'}</dd></div>
                <div><dt>Rate</dt><dd>{money(sub.pricePerUser)} per user</dd></div>
                <div><dt>Seats</dt><dd>{sub.seats}</dd></div>
                <div><dt>Period</dt><dd>{sub.billingPeriod === 'yearly' ? 'Yearly' : 'Monthly'}</dd></div>
                <div><dt>Contract</dt><dd className="strong">{money(sub.contractValue)}</dd></div>
                <div><dt>Status</dt><dd><Pill value={sub.status} /></dd></div>
                <div><dt>This period</dt><dd>{date(sub.currentPeriodStart)} → {date(sub.currentPeriodEnd)}</dd></div>
                {/* Only while they are actually on one. A trial end date
                    left showing on a paying account reads as though the
                    money is about to stop. */}
                {sub.status === 'trialing' && sub.trialEndsOn
                  ? <div><dt>Trial ends</dt><dd>{date(sub.trialEndsOn)}</dd></div>
                  : null}
              </dl>
              {sub.notes ? <p className="pf-notes">{sub.notes}</p> : null}
              <div className="pf-row-actions">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>
                  Change terms
                </button>
                <button
                  type="button" className="btn btn-primary btn-sm" disabled={busy}
                  onClick={() => act(
                    () => api.post(`/platform/companies/${companyId}/invoices`, {}),
                    'Invoice raised',
                  )}
                >
                  Raise invoice for this period
                </button>
              </div>
            </>
          )
        ) : <p className="pf-empty">No subscription.</p>}

        <h3>Invoices</h3>
        {company.invoices.length === 0 ? (
          <p className="pf-empty">Nothing invoiced yet.</p>
        ) : (
          <div className="pf-table-wrap">
            <table className="pf-table">
              <thead>
                <tr>
                  <th>Invoice</th><th>Period</th><th className="num">Total</th>
                  <th>Status</th><th>Due</th><th />
                </tr>
              </thead>
              <tbody>
                {company.invoices.map((i) => (
                  <tr key={i.id}>
                    <td>
                      <strong>{i.invoiceNo}</strong>
                      <span className="pf-sub">
                        {i.seats} × {money(i.pricePerUser)}
                        {i.isInterstate ? ` · IGST ${money(i.igst)}` : ` · CGST+SGST ${money(i.cgst + i.sgst)}`}
                      </span>
                    </td>
                    <td>{date(i.periodStart)}</td>
                    <td className="num strong">{money(i.total)}</td>
                    <td><Pill value={i.status} /></td>
                    <td>{i.paidOn ? `Paid ${date(i.paidOn)}` : date(i.dueOn)}</td>
                    <td className="num">
                      {i.status === 'draft' ? (
                        <button
                          type="button" className="btn btn-ghost btn-sm" disabled={busy}
                          onClick={() => act(
                            () => api.post(`/platform/invoices/${i.id}/status`, { status: 'sent' }),
                            `${i.invoiceNo} marked sent`,
                          )}
                        >
                          Mark sent
                        </button>
                      ) : i.status === 'sent' ? (
                        <button
                          type="button" className="btn btn-ghost btn-sm" disabled={busy}
                          onClick={() => act(
                            () => api.post(`/platform/invoices/${i.id}/status`, {
                              status: 'paid',
                              paidOn: new Date().toISOString().slice(0, 10),
                            }),
                            `${i.invoiceNo} marked paid`,
                          )}
                        >
                          Mark paid
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <h3>Users & passwords</h3>
        <CompanyUsers companyId={companyId} />

        {company.history.length > 0 ? (
          <>
            <h3>History</h3>
            <ul className="pf-history">
              {company.history.map((h, n) => (
                <li key={`${h.at}-${n}`}>
                  <strong>{h.kind.replace(/_/g, ' ')}</strong>
                  <span className="pf-sub">{date(h.at)}{h.actor ? ` · ${h.actor}` : ''}</span>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------

function SettingsTab() {
  const toast = useToast();
  const [form, setForm] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void api.get<Record<string, unknown>>('/platform/settings').then((s) => {
      setForm({
        legalName: String(s.legalName ?? ''),
        gstin: String(s.gstin ?? ''),
        stateName: String(s.stateName ?? ''),
        address: String(s.address ?? ''),
        invoicePrefix: String(s.invoicePrefix ?? ''),
        defaultGstRate: String(s.defaultGstRate ?? 18),
        paymentTermsDays: String(s.paymentTermsDays ?? 15),
        invoiceSeq: String(s.invoiceSeq ?? 0),
      });
    });
  }, []);

  const set = (k: string) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="pf-card">
      <div className="pf-card-head"><h2>What appears on the invoices we raise</h2></div>
      <p className="pf-hint">
        Our own GSTIN decides CGST/SGST against IGST: a customer whose GSTIN starts with a
        different state code is billed IGST.
      </p>
      <div className="pf-fields">
        <label><span>Legal name</span><input className="input" value={form.legalName ?? ''} onChange={set('legalName')} /></label>
        <label><span>Our GSTIN</span><input className="input" value={form.gstin ?? ''} onChange={set('gstin')} maxLength={15} /></label>
        <label><span>State</span><input className="input" value={form.stateName ?? ''} onChange={set('stateName')} /></label>
        <label><span>Invoice prefix</span><input className="input" value={form.invoicePrefix ?? ''} onChange={set('invoicePrefix')} /></label>
        <label><span>GST rate %</span><input className="input" type="number" value={form.defaultGstRate ?? ''} onChange={set('defaultGstRate')} /></label>
        <label><span>Payment terms (days)</span><input className="input" type="number" value={form.paymentTermsDays ?? ''} onChange={set('paymentTermsDays')} /></label>
      </div>
      <label className="pf-full">
        <span>Address</span>
        <textarea className="input" rows={2} value={form.address ?? ''} onChange={set('address')} />
      </label>
      <p className="pf-hint">Last invoice number issued: {form.invoiceSeq}</p>
      <div className="pf-row-actions">
        <button
          type="button" className="btn btn-primary" disabled={saving}
          onClick={async () => {
            setSaving(true);
            try {
              await api.patch('/platform/settings', {
                legalName: form.legalName,
                gstin: form.gstin || null,
                stateName: form.stateName,
                address: form.address || null,
                invoicePrefix: form.invoicePrefix,
                defaultGstRate: Number(form.defaultGstRate),
                paymentTermsDays: Number(form.paymentTermsDays),
              });
              toast.success('Invoice settings saved');
            } catch (err) {
              toast.error(err instanceof Error ? err.message : 'Could not save');
            } finally {
              setSaving(false);
            }
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
